import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { strings } from '../i18n/strings';
import translationEN from '../i18n/locales/en.json';
import translationNL from '../i18n/locales/nl.json';
import translationAR from '../i18n/locales/ar.json';
import type { Language, LanguageStrings } from '../types_i18n';
import { translationMetrics } from '../services/translationMetrics';
import { logger } from '../utils/logger';

// Deep merge function to properly merge nested objects
const deepMerge = (target: any, source: any): any => {
  const output = { ...target };
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
};

const isObject = (item: any): boolean => {
  return item && typeof item === 'object' && !Array.isArray(item);
};

// Merge JSON translations with strings.ts for backward compatibility
// Deep merge to ensure nested objects (like 'practice') are properly merged
const mergedStrings = {
  en: deepMerge(strings.en, translationEN),
  nl: deepMerge(strings.nl, translationNL),
  ar: deepMerge(strings.ar, translationAR),
};

interface LanguageContextType {
  currentLanguage: Language; // Always set (initialized from localStorage, never null)
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
  t_nested: (path: string) => string;
  getVoiceCommands: () => { [key: string]: number };
  getSpeechLang: () => string;
  getSpeechVoice: () => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Initialize language synchronously from localStorage to prevent English flash
  // This ensures the app always opens in the user's chosen language (no exceptions)
  const getInitialLanguage = (): Language => {
    if (typeof window === 'undefined') return 'nl'; // SSR fallback - default to Dutch
    const savedLanguage = localStorage.getItem('preferredLanguage') as Language;
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'nl' || savedLanguage === 'ar')) {
      return savedLanguage;
    }
    return 'nl'; // Default to Dutch if no language is saved
  };
  
  const [currentLanguage, setCurrentLanguage] = useState<Language>(getInitialLanguage());

  useEffect(() => {
    try {
      const savedLanguage = localStorage.getItem('preferredLanguage') as Language | null;
      const isValid = savedLanguage === 'en' || savedLanguage === 'nl' || savedLanguage === 'ar';
      if (!isValid) {
        localStorage.setItem('preferredLanguage', currentLanguage);
      }
    } catch {
      // Ignore localStorage errors (private mode, etc.)
    }
  }, [currentLanguage]);

  // Get nested object value by path (e.g., "dashboard.welcomeMessage")
  const getNestedValue = useCallback((obj: any, path: string): string | undefined => {
    const keys = path.split('.');
    let current = obj;
    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return undefined;
      }
    }
    return typeof current === 'string' ? current : undefined;
  }, []);

  // Enhanced translation function with quality tracking (memoized)
  const t = useCallback((key: string): string => {
    // Always use currentLanguage (guaranteed to be set from initialization)
    const language = currentLanguage;
    const langStrings = mergedStrings[language];
    const value = langStrings[key as keyof LanguageStrings];
    const translatedText = typeof value === 'string' ? value : key;
    
    // Track translation usage for analytics
    if (language && translatedText !== key) {
      translationMetrics.trackTranslation(
        key,
        language,
        key, // original text (key)
        translatedText
      );
    }
    
    return translatedText;
  }, [currentLanguage]);

  // Nested translation function (memoized)
  const t_nested = useCallback((path: string): string => {
    // Always use currentLanguage (guaranteed to be set from initialization)
    const language = currentLanguage;
    const langStrings = mergedStrings[language];
    
    // Try to get translation from current language
    const translated = getNestedValue(langStrings, path);
    if (translated && typeof translated === 'string') {
      return translated;
    }
    
    // Fallback: English (not for Arabic UI — Arabic users must not see EN/NL fallback strings)
    if (language !== 'en' && language !== 'ar') {
      const enTranslated = getNestedValue(mergedStrings.en, path);
      if (enTranslated && typeof enTranslated === 'string') {
        return enTranslated;
      }
    }
    
    // Last resort: return the path (translation key) - this should not happen if translations are correct
    logger.warn(`Translation not found for key: ${path} in language: ${language}`);
    return path;
  }, [currentLanguage, getNestedValue]);

  // Get voice commands for speech recognition (memoized)
  const getVoiceCommands = useCallback(() => {
    // currentLanguage is always set (initialized from localStorage)
    const commands = mergedStrings[currentLanguage].voiceCommands;
    return {
      [commands.optionA]: 0,
      [commands.optionB]: 1,
      [commands.optionC]: 2,
      [commands.optionD]: 3,
      [commands.first]: 0,
      [commands.second]: 1,
      [commands.third]: 2,
      [commands.fourth]: 3,
      [commands.one]: 0,
      [commands.two]: 1,
      [commands.three]: 2,
      [commands.four]: 3,
    };
  }, [currentLanguage]);

  const getSpeechLang = useCallback((): string => {
    // Voice/STT removed — return base language code
    return currentLanguage === 'nl' ? 'nl-NL' : currentLanguage === 'ar' ? 'ar-SA' : 'en-AU';
  }, [currentLanguage]);

  // Web Speech removed — return empty (voice selection unused with cloud TTS)
  const getSpeechVoice = useCallback((): string => '', []);

  const setLanguage = useCallback((language: Language) => {
    setCurrentLanguage(language);
    // Store in localStorage for persistence
    localStorage.setItem('preferredLanguage', language);
    // Update HTML attributes immediately for RTL/LTR.
    // IMPORTANT: Only Arabic uses RTL, English and Dutch use LTR.
    // Marketing landing intentionally stays LTR regardless of app language.
    const html = document.documentElement;
    if (html.getAttribute('data-marketing-scroll') === 'true') {
      return;
    }
    const isRTL = language === 'ar';
    html.dir = isRTL ? 'rtl' : 'ltr';
    html.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    html.setAttribute('lang', language);
  }, []);

  // Ensure HTML attributes are set immediately on mount (already set in index.tsx, but ensure consistency)
  // This prevents any flash of wrong language or direction
  useEffect(() => {
    const language = currentLanguage; // Always set (initialized from localStorage)
    const html = document.documentElement;
    // Marketing landing intentionally stays LTR regardless of app language.
    if (html.getAttribute('data-marketing-scroll') === 'true') {
      return;
    }
    const isRTL = language === 'ar';
    html.dir = isRTL ? 'rtl' : 'ltr';
    html.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    html.setAttribute('lang', language);
    
    // Ensure localStorage is set if it wasn't already
    if (!localStorage.getItem('preferredLanguage')) {
      localStorage.setItem('preferredLanguage', language);
    }
  }, [currentLanguage]);

  // Memoize context value to prevent unnecessary re-renders
  const value: LanguageContextType = useMemo(() => ({
    currentLanguage,
    setLanguage,
    t,
    t_nested,
    getVoiceCommands,
    getSpeechLang,
    getSpeechVoice,
  }), [currentLanguage, setLanguage, t, t_nested, getVoiceCommands, getSpeechLang, getSpeechVoice]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}; 